import { useCallback, useState } from 'react';

// ----------------------------------------------------------------------
// Reusable column show/hide for admin tables. Pass the full column registry
// and a unique storage key; the choice persists in localStorage so each table
// remembers what the admin chose to show.

export type ColumnDef = {
  id: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  // Off until the admin turns it on (a wide table keeps its key columns in view).
  defaultHidden?: boolean;
};

export default function useColumnVisibility(columns: ColumnDef[], storageKey: string) {
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch {
      /* ignore */
    }
    return columns.filter((c) => !c.defaultHidden).map((c) => c.id);
  });

  const toggleColumn = useCallback(
    (id: string) => {
      setVisibleColumns((prev) => {
        const next = prev.includes(id) ? prev.filter((k) => k !== id) : [...prev, id];
        try {
          localStorage.setItem(storageKey, JSON.stringify(next));
        } catch {
          /* ignore */
        }
        return next;
      });
    },
    [storageKey]
  );

  const isVisible = useCallback(
    (id: string) => visibleColumns.includes(id),
    [visibleColumns]
  );

  // Header cells for the visible columns, in registry order. Callers append
  // any fixed trailing column (e.g. an actions cell) themselves.
  const headLabel = columns.filter((c) => visibleColumns.includes(c.id));

  return { visibleColumns, toggleColumn, isVisible, headLabel };
}
